import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Trash2, Plus, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LANGS = [
  { code: 'ca', label: 'Català' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
];

const EMPTY_ML = { ca: '', es: '', en: '', fr: '' };

/** Multilingual field with language tabs. */
const MLField = ({ label, value, onChange, multiline = false, placeholder = '' }) => {
  const [lang, setLang] = useState('ca');
  const v = value || EMPTY_ML;
  const InputComp = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${
                lang === l.code
                  ? 'bg-emerald-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {l.code}
            </button>
          ))}
        </div>
      </div>
      <InputComp
        value={v[lang] || ''}
        onChange={(e) => onChange({ ...v, [lang]: e.target.value })}
        placeholder={placeholder || `Texto en ${LANGS.find((x) => x.code === lang)?.label}`}
        className={multiline ? 'min-h-[88px]' : ''}
        data-testid={`sig-field-${label.toLowerCase().replace(/\s+/g, '-')}-${lang}`}
      />
    </div>
  );
};

export const SigContentForm = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/sig-content`);
        setContent(r.data);
      } catch (e) {
        toast.error('Error cargando contenido SIG');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const update = (field, value) => setContent((c) => ({ ...c, [field]: value }));

  const updateBulletList = (field, index, val) => {
    const list = [...(content[field] || [])];
    list[index] = val;
    update(field, list);
  };

  const addBullet = (field) => update(field, [...(content[field] || []), { ...EMPTY_ML }]);
  const removeBullet = (field, index) => {
    const list = [...(content[field] || [])];
    list.splice(index, 1);
    update(field, list);
  };

  const updateGallery = (index, key, val) => {
    const list = [...(content.gallery || [])];
    list[index] = { ...list[index], [key]: val };
    update('gallery', list);
  };
  const addGallery = () => update('gallery', [...(content.gallery || []), { url: '', caption: '' }]);
  const removeGallery = (index) => {
    const list = [...(content.gallery || [])];
    list.splice(index, 1);
    update('gallery', list);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/sig-content`, content, { headers: authHeaders });
      toast.success('Contenido SIG guardado');
    } catch (e) {
      toast.error('Error guardando: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-10 text-center text-slate-500">Cargando...</div>;
  if (!content) return <div className="py-10 text-center text-red-500">No se pudo cargar el contenido</div>;

  return (
    <div className="space-y-6" data-testid="sig-content-form">
      {/* Save bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 py-3 px-1 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Funnel SIG — Vols jugar el teu primer forat?</h3>
          <p className="text-xs text-slate-500">Editor de contenido del funnel UGOLF Aravell Andorra</p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800"
          data-testid="sig-save-btn"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {/* HERO */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">🏌️ Hero</CardTitle>
          <CardDescription>Cabecera principal de la página</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Eyebrow" value={content.eyebrow} onChange={(v) => update('eyebrow', v)} />
          <MLField label="Título Hero" value={content.hero_title} onChange={(v) => update('hero_title', v)} />
          <MLField label="Subtítulo" value={content.hero_subtitle} onChange={(v) => update('hero_subtitle', v)} multiline />
          <MLField label="Intro" value={content.hero_intro} onChange={(v) => update('hero_intro', v)} multiline />
        </CardContent>
      </Card>

      {/* PRICE */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">💰 Precio</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Precio</Label>
            <Input value={content.price || ''} onChange={(e) => update('price', e.target.value)} placeholder="60€" data-testid="sig-price" />
          </div>
          <MLField label="Etiqueta precio" value={content.price_label} onChange={(v) => update('price_label', v)} />
        </CardContent>
      </Card>

      {/* SOCIAL PROOF */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">⭐ Prueba social</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Número</Label>
            <Input value={content.social_proof_number || ''} onChange={(e) => update('social_proof_number', e.target.value)} placeholder="330+" data-testid="sig-social-number" />
          </div>
          <MLField label="Texto" value={content.social_proof_text} onChange={(v) => update('social_proof_text', v)} multiline />
          <div>
            <Label className="text-sm font-medium">URL Vídeo (mp4 o stream)</Label>
            <Input value={content.video_url || ''} onChange={(e) => update('video_url', e.target.value)} placeholder="https://..." data-testid="sig-video-url" />
            <p className="text-xs text-slate-500 mt-1">Format suportat: MP4 directe (Cloudinary, S3, etc.). Si no es veu, comprova que la URL acaba en <code>.mp4</code> i que respon CORS.</p>
          </div>
          <div>
            <Label className="text-sm font-medium">URL Pòster (opcional)</Label>
            <Input value={content.video_poster_url || ''} onChange={(e) => update('video_poster_url', e.target.value)} placeholder="https://...jpg" data-testid="sig-video-poster" />
            <p className="text-xs text-slate-500 mt-1">Imatge de previsualització que es veu abans de prémer play. Si la URL del vídeo és Cloudinary, pots derivar-la canviant <code>.mp4</code> per <code>.jpg</code>.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="sig-video-autoplay"
              type="checkbox"
              checked={!!content.video_autoplay}
              onChange={(e) => update('video_autoplay', e.target.checked)}
              data-testid="sig-video-autoplay"
            />
            <Label htmlFor="sig-video-autoplay" className="cursor-pointer text-sm">▶ Autoplay mut + loop (hero-style)</Label>
          </div>
          <div>
            <Label className="text-sm font-medium">Saltar primers segons (skip intro)</Label>
            <Input
              type="number"
              min="0"
              value={content.video_start_seconds ?? 0}
              onChange={(e) => update('video_start_seconds', parseInt(e.target.value || '0', 10))}
              placeholder="0"
              data-testid="sig-video-start"
            />
            <p className="text-xs text-slate-500 mt-1">Ex: 3 = el vídeo comença al segon 3 (per saltar el rètol "CAPS").</p>
          </div>
        </CardContent>
      </Card>

      {/* INCLUDED */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">✅ Qué incluye</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título sección" value={content.included_title} onChange={(v) => update('included_title', v)} />
          <div className="space-y-3">
            {(content.included_items || []).map((it, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Item #{i + 1}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeBullet('included_items', i)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
                <MLField label="" value={it} onChange={(v) => updateBulletList('included_items', i, v)} />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addBullet('included_items')} data-testid="sig-add-included">
              <Plus className="h-4 w-4 mr-2" /> Añadir item
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* COURSE */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">⛳ Campo (UGOLF Aravell)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título" value={content.course_title} onChange={(v) => update('course_title', v)} />
          <MLField label="Texto" value={content.course_text} onChange={(v) => update('course_text', v)} multiline />
          <div className="space-y-3">
            <Label className="text-sm font-medium">Bullets</Label>
            {(content.course_bullets || []).map((b, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Bullet #{i + 1}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeBullet('course_bullets', i)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
                <MLField label="" value={b} onChange={(v) => updateBulletList('course_bullets', i, v)} />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addBullet('course_bullets')}>
              <Plus className="h-4 w-4 mr-2" /> Añadir bullet
            </Button>
          </div>
          <div>
            <Label className="text-sm font-medium">URL Logo Partner (Ugolf Aravell)</Label>
            <Input value={content.partner_logo_url || ''} onChange={(e) => update('partner_logo_url', e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <Label className="text-sm font-medium">Nombre Partner</Label>
            <Input value={content.partner_name || ''} onChange={(e) => update('partner_name', e.target.value)} placeholder="UGOLF Aravell Andorra" />
          </div>
        </CardContent>
      </Card>

      {/* AFTER */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">🔮 Después de la SIG</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título" value={content.after_title} onChange={(v) => update('after_title', v)} />
          <MLField label="Texto" value={content.after_text} onChange={(v) => update('after_text', v)} multiline />
        </CardContent>
      </Card>

      {/* CTA */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">📞 CTA WhatsApp</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Texto botón" value={content.cta_text} onChange={(v) => update('cta_text', v)} />
          <div>
            <Label className="text-sm font-medium">Número WhatsApp (sin +, ej. 34664368737)</Label>
            <Input value={content.whatsapp_number || ''} onChange={(e) => update('whatsapp_number', e.target.value)} placeholder="34664368737" data-testid="sig-whatsapp-number" />
          </div>
          <MLField label="Mensaje WhatsApp prellenado" value={content.whatsapp_message} onChange={(v) => update('whatsapp_message', v)} multiline />
        </CardContent>
      </Card>

      {/* GALLERY */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">🖼️ Galería</CardTitle>
          <CardDescription>Sube imágenes a Cloudinary/Imgur y pega las URLs aquí</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(content.gallery || []).map((g, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Imagen #{i + 1}</span>
                <Button size="sm" variant="ghost" onClick={() => removeGallery(i)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
              <Input value={g.url} onChange={(e) => updateGallery(i, 'url', e.target.value)} placeholder="URL imagen" />
              <Input value={g.caption || ''} onChange={(e) => updateGallery(i, 'caption', e.target.value)} placeholder="Caption (opcional)" />
              {g.url && (
                <img src={g.url} alt="" className="w-24 h-24 object-cover rounded border border-slate-200" />
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addGallery} data-testid="sig-add-gallery">
            <Plus className="h-4 w-4 mr-2" /> Añadir imagen
          </Button>
        </CardContent>
      </Card>

      {/* Bottom save */}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
};

export default SigContentForm;
